import { useEffect, useState } from 'react';

import { readStored, writeStored } from './navigation-storage';

export const OPEN_GROUP_STORAGE_KEY = 'workspace-navigation-open-group';

// A navigation between route roots remounts the rail. Once the rail has mounted in this page load, a remount starts
// from the group shown last, so the new page's group slides open as on a click; a fresh load shows it open at once.
let hasMounted = false;

/**
 * The rail's groups as an accordion: at most one is open, and only that one is remembered. Going to another entry
 * inside a group opens that group and closes the rest, as a click on it would. Folding the open group leaves none
 * open until the next such navigation.
 */
export const useOpenGroup = <Group extends string>({
    activeEntry,
    activeGroup,
    groups,
}: {
    /** The rail entry the current page belongs to, so moving to another entry opens its group again. */
    readonly activeEntry: string | null;
    /** The group holding that entry, if it is in one. */
    readonly activeGroup: Group | null;
    /** Every group the rail can show; a stored value outside them is ignored. */
    readonly groups: readonly Group[];
}): { readonly openGroup: Group | null; readonly toggleGroup: (group: Group) => void } => {
    const [openGroup, setOpenGroup] = useState<Group | null>(() => {
        const stored = readStored('localStorage', OPEN_GROUP_STORAGE_KEY);
        const storedGroup = groups.find((group) => group === stored) ?? null;

        return hasMounted ? storedGroup : (activeGroup ?? storedGroup);
    });

    useEffect(() => {
        hasMounted = true;
    }, []);

    useEffect(() => {
        if (activeEntry !== null && activeGroup !== null) {
            setOpenGroup(activeGroup);
        }
    }, [activeEntry, activeGroup]);

    useEffect(() => {
        writeStored('localStorage', OPEN_GROUP_STORAGE_KEY, openGroup);
    }, [openGroup]);

    return {
        openGroup,
        toggleGroup: (group) => setOpenGroup((current) => (current === group ? null : group)),
    };
};
